import { useEffect, useMemo, useRef, useState } from "react";
import { ExternalLink } from "lucide-react";
import { ADDRESSES } from "../lib/config";
import { useTokenStats } from "@/hooks/useTokenStats";

type Point = { t: number; p: number }; // t = block/unix ordinal, p = native (ETH/PIGGY)

/// Live PIGGY/WETH price chart built from on-chain Uniswap Sync events. We
/// render this ourselves because DexScreener/GeckoTerminal don't have OHLCV
/// candles for this pool yet on a chain this young — their embeds render blank.
///
/// Hardened against both upstream hiccups:
///  - if the USD price (DexScreener-backed, rate-limits under load) is missing,
///    the last known scale factor is reused, else the chart plots native ETH
///    prices — it NEVER multiplies by a zero factor (which used to flatline
///    the whole chart at $0.00 and look completely broken);
///  - a failed pair-history refresh keeps the last good series on screen
///    instead of collapsing to "not enough trades".
export function DexScreenerChart({ className = "" }: { className?: string }) {
    const pair = ADDRESSES.PIGGY_PAIR;
    const { stats } = useTokenStats();
    const [points, setPoints] = useState<Point[]>([]);
    const [loaded, setLoaded] = useState(false);
    // Last good USD-per-native factor — survives transient priceUsd outages.
    const factorRef = useRef(0);

    useEffect(() => {
        if (!pair) return;
        let cancelled = false;
        const load = () => {
            fetch("/.netlify/functions/pair-history", { signal: AbortSignal.timeout(25_000) })
                .then((r) => r.json())
                .then((d) => {
                    if (cancelled) return;
                    // Keep any point with a real price; timestamps may be 0 on
                    // some RPCs — the chart falls back to ordinal x in that case.
                    const pts = (d?.points ?? []).filter((x: Point) => x.p > 0);
                    // Never replace a good series with a worse one from a
                    // flaky refresh — only grow/replace when it's real data.
                    setPoints((prev) => (pts.length >= 2 || prev.length === 0 ? pts : prev));
                    setLoaded(true);
                })
                .catch(() => { if (!cancelled) setLoaded(true); });
        };
        load();
        const id = setInterval(load, 60_000);
        return () => { cancelled = true; clearInterval(id); };
    }, [pair]);

    const lastNative = points.length > 0 ? points[points.length - 1].p : 0;
    if (stats.priceUsd > 0 && lastNative > 0) {
        factorRef.current = stats.priceUsd / lastNative;
    }
    const factor = factorRef.current;
    const inUsd = factor > 0;

    // Scale native prices to USD when a live (or last-known) USD price exists;
    // otherwise plot the native ETH series — same shape, honest units.
    const chartPoints = useMemo(() => {
        if (points.length === 0) return [];
        return points.map((pt) => ({ t: pt.t, v: inUsd ? pt.p * factor : pt.p }));
    }, [points, factor, inUsd]);

    if (!pair) return null;
    const dexUrl = `https://dexscreener.com/robinhood/${pair}`;

    return (
        <div className={className}>
            <div className="flex items-center justify-between mb-3 px-1">
                <h2 className="display text-lg font-black text-piggy-cream">Live chart</h2>
                <a
                    href={dexUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1 text-xs text-piggy-cream/60 hover:text-piggy-cream transition-colors"
                >
                    DexScreener <ExternalLink className="w-3 h-3" />
                </a>
            </div>
            <div className="rounded-2xl ring-1 ring-white/[0.08] bg-piggy-black/40 p-4">
                <PriceLine points={chartPoints} loaded={loaded} inUsd={inUsd}
                    currentUsd={stats.priceUsd > 0 ? stats.priceUsd : (inUsd ? lastNative * factor : 0)} />
            </div>
        </div>
    );
}

function PriceLine({
    points, loaded, inUsd, currentUsd,
}: { points: { t: number; v: number }[]; loaded: boolean; inUsd: boolean; currentUsd: number }) {
    const W = 800, H = 320, PAD = 8;

    if (!loaded && points.length === 0) {
        return <div className="h-[280px] grid place-items-center text-piggy-cream/40 text-sm">Loading chart…</div>;
    }
    if (points.length < 2) {
        return (
            <div className="h-[280px] grid place-items-center text-center text-piggy-cream/50 text-sm px-6">
                Not enough trades yet to plot a chart.<br />
                <span className="text-piggy-cream/30 text-xs mt-1">
                    It fills in as trading picks up — current price{" "}
                    {currentUsd > 0 ? `$${currentUsd.toPrecision(3)}` : "—"}.
                </span>
            </div>
        );
    }

    const ts = points.map((p) => p.t);
    const vs = points.map((p) => p.v);
    const tMin = Math.min(...ts), tMax = Math.max(...ts);
    // If timestamps don't vary (all 0 / missing), plot by ordinal index instead
    // so the line still spreads across the width.
    const hasTime = tMax > tMin;
    let vMin = Math.min(...vs), vMax = Math.max(...vs);
    if (vMin === vMax) { vMin *= 0.98; vMax *= 1.02; } // flat series — give it height
    const pad = (vMax - vMin) * 0.12;
    vMin -= pad; vMax += pad;

    const n = points.length;
    const xAt = (i: number, t: number) =>
        PAD + (hasTime ? (t - tMin) / (tMax - tMin) : i / (n - 1 || 1)) * (W - 2 * PAD);
    const y = (v: number) => PAD + (1 - (v - vMin) / (vMax - vMin || 1)) * (H - 2 * PAD);

    const line = points.map((p, i) => `${i === 0 ? "M" : "L"}${xAt(i, p.t).toFixed(1)},${y(p.v).toFixed(1)}`).join(" ");
    const area = `${line} L${(W - PAD).toFixed(1)},${H - PAD} L${PAD},${H - PAD} Z`;

    const first = vs[0], last = vs[vs.length - 1];
    const changePct = first > 0 ? ((last - first) / first) * 100 : 0;
    const up = changePct >= 0;
    const fmt = (v: number) => inUsd ? `$${v.toPrecision(3)}` : `${v.toPrecision(3)} ETH`;

    return (
        <div>
            <div className="flex items-baseline gap-3 mb-2 px-1">
                <span className="display text-2xl font-black text-piggy-cream">{fmt(last)}</span>
                <span className={`text-sm font-semibold ${up ? "text-piggy-pink-300" : "text-red-400"}`}>
                    {up ? "▲" : "▼"} {Math.abs(changePct).toFixed(1)}%
                </span>
                <span className="text-xs text-piggy-cream/40 ml-auto">
                    {points.length} trades · since launch
                </span>
            </div>
            <svg viewBox={`0 0 ${W} ${H}`} className="w-full h-[280px]" preserveAspectRatio="none">
                <defs>
                    <linearGradient id="pl-fill" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="#ccff00" stopOpacity="0.28" />
                        <stop offset="100%" stopColor="#ccff00" stopOpacity="0" />
                    </linearGradient>
                </defs>
                <path d={area} fill="url(#pl-fill)" />
                <path d={line} fill="none" stroke="#ccff00" strokeWidth="2.5"
                      strokeLinejoin="round" strokeLinecap="round" vectorEffect="non-scaling-stroke" />
            </svg>
        </div>
    );
}
